import { additionalNavigationParamsSchema } from 'components/Procedure/Header/NavigationPanel/additionalNavigationParamsSchema';
import { MAX_SIDE_BY_SIDE_VIEWERS } from 'components/Procedure/SlidesViewer/constants';
import { omit, size, slice } from 'es-toolkit/compat';
import { useCallback } from 'react';
import { useActiveAnnotationAssignmentForViewer } from 'services/annotations/useAnnotationQueryParams';
import {
  BooleanParam,
  DelimitedArrayParam,
  NumberParam,
  StringParam,
  useQueryParam,
  useQueryParams,
} from 'use-query-params';
import { useEncodedFilters } from './useEncodedFilters';

interface LiveViewerStateSyncOptions {
  preservedTargetParamKeys?: string[];
  omittedTargetParamKeys?: string[];
}

const getCurrentHashSearchParams = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const currentHash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
  const queryString = currentHash.includes('?') ? currentHash.slice(currentHash.indexOf('?') + 1) : '';

  if (!queryString) {
    return undefined;
  }

  return new URLSearchParams(queryString);
};

const getSideBySideViewerValues = <T>(values?: T[] | null, limit = MAX_SIDE_BY_SIDE_VIEWERS): T[] | undefined =>
  values ? slice(values, 0, limit) : undefined;

export const syncNavigationTargetWithLiveViewerState = (
  target: string,
  { preservedTargetParamKeys = [], omittedTargetParamKeys = [] }: LiveViewerStateSyncOptions = {}
) => {
  if (!target?.includes('?')) {
    return target;
  }

  const currentParams = getCurrentHashSearchParams();
  if (!currentParams) {
    return target;
  }

  const [targetPath, targetQueryString] = target.split('?');
  const targetParams = new URLSearchParams(targetQueryString || '');
  const preservedTargetParamKeysSet = new Set(preservedTargetParamKeys);
  const omittedTargetParamKeysSet = new Set(omittedTargetParamKeys);
  const syncCandidateParamKeys = new Set([...Array.from(targetParams.keys()), ...Array.from(currentParams.keys())]);

  syncCandidateParamKeys.forEach((key) => {
    if (omittedTargetParamKeysSet.has(key)) {
      targetParams.delete(key);
      return;
    }

    if (preservedTargetParamKeysSet.has(key)) {
      return;
    }

    const value = currentParams.get(key);
    if (value === null) {
      targetParams.delete(key);
      return;
    }

    targetParams.set(key, value);
  });

  return `${targetPath}?${targetParams.toString()}`;
};

export const getSelectedSlideIdsForSlideNavigation = ({
  currentSelectedSlideIds,
  targetSlideId,
}: {
  currentSelectedSlideIds?: string[];
  targetSlideId: string;
}) => {
  const currentSideBySideSelectedSlideIds = getSideBySideViewerValues(currentSelectedSlideIds) || [];

  if (!currentSideBySideSelectedSlideIds.length) {
    return [targetSlideId];
  }

  const [currentPrimarySlideId, ...secondarySelectedSlideIds] = currentSideBySideSelectedSlideIds;

  if (currentSideBySideSelectedSlideIds.includes(targetSlideId)) {
    // If the target is already visible, promote one visible instance to the primary viewer without collapsing mirrored target slots.
    let hasReplacedVisibleTarget = false;

    return [
      targetSlideId,
      ...secondarySelectedSlideIds.map((slideId) => {
        if (!hasReplacedVisibleTarget && slideId === targetSlideId) {
          hasReplacedVisibleTarget = true;
          return currentPrimarySlideId;
        }

        return slideId;
      }),
    ];
  }

  // Otherwise replace the primary viewer while preserving the rest of the layout, including mirrored split-view slots.
  return [
    targetSlideId,
    ...secondarySelectedSlideIds.map((slideId) => (slideId === currentPrimarySlideId ? targetSlideId : slideId)),
  ];
};

export const getSelectedSlideIdsForCaseAwareSlideNavigation = ({
  currentSelectedSlideIds,
  targetSlideId,
  currentCaseId,
  targetCaseId,
  preferredTargetCaseSelectedSlideIds,
}: {
  currentSelectedSlideIds?: string[];
  targetSlideId: string;
  currentCaseId?: number | null;
  targetCaseId?: number | null;
  preferredTargetCaseSelectedSlideIds?: string[];
}) => {
  const selectedSlideIdsForLayout = getSelectedSlideIdsForSlideNavigation({
    currentSelectedSlideIds,
    targetSlideId,
  });

  const navigatingAcrossCases =
    currentCaseId !== undefined &&
    currentCaseId !== null &&
    targetCaseId !== undefined &&
    targetCaseId !== null &&
    currentCaseId !== targetCaseId;

  if (!navigatingAcrossCases || !preferredTargetCaseSelectedSlideIds?.length) {
    return selectedSlideIdsForLayout;
  }

  return selectedSlideIdsForLayout.map((slideId, viewerIndex) => {
    if (viewerIndex === 0 || slideId === targetSlideId) {
      return targetSlideId;
    }

    return preferredTargetCaseSelectedSlideIds[viewerIndex] || targetSlideId;
  });
};

export interface CasePageUrlProps {
  caseId: number;
  caseStudyId?: string;
  slideIdForReviewing?: string;
  selectedSlideIds?: string[];
  newPage?: number;
  labId?: string;
  viewersStainPreferences?: string[];
}

export interface SlidePageUrlProps {
  slideId: string;
  selectedSlideIds?: string[];
  viewersStainPreferences?: string[];
  caseId?: number; // caseId and caseStudyId is optional because pending slide doesn't have a case
  caseStudyId?: string;
  clearCurrentStudyFilter?: boolean;
  newPage?: number;
  labId?: string;
  activeAnnotationAssignment?: number;
}

export const useNavigationToViewerPage = () => {
  const { queryParams, generateEncodedParams } = useEncodedFilters();
  const [searchTerm] = useQueryParam('searchTerm', StringParam);
  const [additionalNavigationParams] = useQueryParams(additionalNavigationParamsSchema);
  const [currentViewerAssignmentId] = useActiveAnnotationAssignmentForViewer(0);
  const [pendingSlidesMode] = useQueryParam('pendingSlidesMode', BooleanParam);

  const getSearchStringForViewerPage = useCallback(
    ({
      slideIdForReviewing,
      selectedSlideIds,
      newPage,
      caseId,
      caseStudyId,
      fromStudyFilterOverride,
      clearCurrentStudyFilter,
      labId,
      activeAnnotationAssignment,
      viewersStainPreferences,
    }: {
      slideIdForReviewing?: string;
      selectedSlideIds?: string[];
      newPage?: number;
      caseId?: number; // this is for slide page (no need to save this twice in case page)
      caseStudyId?: string;
      fromStudyFilterOverride?: string;
      clearCurrentStudyFilter?: boolean;
      labId?: string;
      activeAnnotationAssignment?: number;
      viewersStainPreferences?: string[];
    }): string => {
      newPage = newPage ?? queryParams.page;
      const selectedSlideIdsForViewer = getSideBySideViewerValues(selectedSlideIds);
      const viewerStateLimit = selectedSlideIdsForViewer ? Math.max(size(selectedSlideIdsForViewer), 1) : undefined;
      const {
        viewersStainPreferences: additionalViewersStainPreferences,
        fromStudyFilter: currentFromStudyFilter,
        ...additionalNavigationParamsWithoutViewers
      } = additionalNavigationParams;
      const viewersStainPreferencesForViewer = getSideBySideViewerValues(
        viewersStainPreferences || additionalViewersStainPreferences,
        viewerStateLimit
      );

      const sourceFilters = clearCurrentStudyFilter ? omit(queryParams.filters, 'studyId') : queryParams.filters;

      const filtersWithCaseStudyId = sourceFilters
        ? {
            ...sourceFilters,
            ...(caseStudyId ? { studyId: caseStudyId } : {}),
          }
        : caseStudyId
        ? { studyId: caseStudyId }
        : undefined;

      // `clearCurrentStudyFilter` only removes the authoritative live study filter.
      // Keep `fromStudyFilter` as the navigation/recovery hint unless a more specific override is provided.
      const fromStudyFilter = fromStudyFilterOverride ?? currentFromStudyFilter ?? queryParams.filters?.studyId;

      const encodedParams = generateEncodedParams(
        {
          ...(filtersWithCaseStudyId ? { filters: filtersWithCaseStudyId } : {}),
          ...(newPage ? { page: newPage } : {}),
          ...(labId ? { labId } : {}),
        },
        {
          ...additionalNavigationParamsWithoutViewers,
          ...(fromStudyFilter ? { fromStudyFilter: fromStudyFilter } : {}),
          ...(slideIdForReviewing ? { slideIdForReviewing: slideIdForReviewing } : {}),
          ...(selectedSlideIdsForViewer ? { selectedSlideIds: selectedSlideIdsForViewer } : {}),
          ...(viewersStainPreferencesForViewer ? { viewersStainPreferences: viewersStainPreferencesForViewer } : {}),
          ...(caseId ? { caseId: caseId } : {}),
          ...(searchTerm ? { searchTerm } : {}),
          ...(pendingSlidesMode ? { pendingSlidesMode: true } : {}),
          // Keep an assignment already chosen for this dataset visit when the slide link does not name another one.
          activeAnnotationAssignmentIdViewer0: activeAnnotationAssignment ?? currentViewerAssignmentId ?? undefined,
        },
        {
          ...additionalNavigationParamsSchema,
          ...(pendingSlidesMode ? { pendingSlidesMode: BooleanParam } : {}),
          searchTerm: StringParam,
          caseId: NumberParam,
          selectedSlideIds: DelimitedArrayParam,
          viewersStainPreferences: DelimitedArrayParam,
          activeAnnotationAssignmentIdViewer0: NumberParam,
        }
      );

      return encodedParams;
    },
    [
      additionalNavigationParams,
      generateEncodedParams,
      currentViewerAssignmentId,
      queryParams.filters,
      queryParams.page,
      searchTerm,
      pendingSlidesMode,
    ]
  );

  const getLinkToCasePage = useCallback(
    ({
      caseId,
      caseStudyId,
      slideIdForReviewing,
      selectedSlideIds,
      newPage,
      labId,
      viewersStainPreferences,
    }: CasePageUrlProps) => {
      const encodedParams = getSearchStringForViewerPage({
        slideIdForReviewing,
        selectedSlideIds,
        newPage,
        caseStudyId,
        labId,
        viewersStainPreferences,
      });

      return {
        pathname: `/procedures/${caseId}`,
        search: `?${encodedParams}`,
        hash: '',
      };
    },
    [getSearchStringForViewerPage]
  );

  const getUrlToCasePage = useCallback(
    (props: CasePageUrlProps) => {
      const linkToCasePage = getLinkToCasePage(props);
      return `${linkToCasePage.pathname}${linkToCasePage.search}`;
    },
    [getLinkToCasePage]
  );

  const getLinkToSlidePage = useCallback(
    ({
      slideId,
      selectedSlideIds,
      viewersStainPreferences,
      caseId,
      caseStudyId,
      clearCurrentStudyFilter,
      newPage,
      labId,
      activeAnnotationAssignment,
    }: SlidePageUrlProps) => {
      const shouldUseCaseStudyIdAsLiveFilter = Boolean(caseId);
      const shouldClearStudyFilterForPendingSlide = Boolean(pendingSlidesMode && !caseId);
      const newSearchString = getSearchStringForViewerPage({
        selectedSlideIds: selectedSlideIds || [slideId],
        viewersStainPreferences,
        newPage,
        caseId,
        caseStudyId: shouldUseCaseStudyIdAsLiveFilter ? caseStudyId : undefined,
        fromStudyFilterOverride: !shouldUseCaseStudyIdAsLiveFilter ? caseStudyId : undefined,
        clearCurrentStudyFilter: clearCurrentStudyFilter || shouldClearStudyFilterForPendingSlide,
        labId,
        activeAnnotationAssignment,
      });

      return {
        pathname: `/slides/${slideId}`,
        search: `?${newSearchString}`,
        hash: '',
      };
    },
    [getSearchStringForViewerPage, pendingSlidesMode]
  );

  const getUrlToSlidePage = useCallback(
    (props: SlidePageUrlProps) => {
      const linkToSlidePage = getLinkToSlidePage(props);
      return `${linkToSlidePage.pathname}${linkToSlidePage.search}`;
    },
    [getLinkToSlidePage]
  );

  return {
    getLinkToCasePage,
    getUrlToCasePage,
    getLinkToSlidePage,
    getUrlToSlidePage,
  };
};
